import React, { useMemo } from 'react';
import {
    ResponsiveContainer,
    ComposedChart,
    ScatterChart as RechartsScatterChart,
    Scatter,
    Line,
    XAxis,
    YAxis,
    ZAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    Label,
    Area,
    Cell,
    ReferenceLine,
    ErrorBar
} from 'recharts';
import { ChartConfig, ScientificChartConfig } from '../../../../types';
import { DEFAULT_SCIENTIFIC_CONFIG, SCIENTIFIC_COLOR_PALETTES, LINE_DASH_ARRAYS } from '../config/scientificDefaults';
import { ScientificMarker } from './ScientificMarker';

interface ScientificScatterPlotProps {
    config: ChartConfig;
    scientificConfig: ScientificChartConfig;
    data: any[];
    onPointClick?: (point: any) => void;
}

/**
 * Scientific Scatter Plot (2D)
 * Matplotlib-style scatter with color/size encoding, multiple series, and scientific axis formatting
 */
export const ScientificScatterPlot: React.FC<ScientificScatterPlotProps> = ({
    config,
    scientificConfig,
    data,
    onPointClick
}) => {
    const { fieldMappings, axisOptions, style, legend, animation, errorBars } = scientificConfig;
    const chartColor = config.customization?.color || '#f59e0b'; // WeaverBI gold

    console.log('[ScientificScatterPlot] fieldMappings:', fieldMappings);
    console.log('[ScientificScatterPlot] colorField:', fieldMappings.colorField);
    console.log('[ScientificScatterPlot] sizeField:', fieldMappings.sizeField);
    console.log('[ScientificScatterPlot] sample data:', data?.[0]);
    console.log('[ScientificScatterPlot] errorBars config:', errorBars);

    // Process data for scatter plot
    const { processedData, seriesMap, colorScale, sizeScale, minValue, maxValue } = useMemo(() => {
        if (!data || data.length === 0 || !fieldMappings.xField || !fieldMappings.yField) {
            return { processedData: [], seriesMap: new Map(), colorScale: null, sizeScale: null, minValue: 0, maxValue: 1 };
        }

        // Extract values for color/size scaling
        let colorValues: number[] = [];
        let sizeValues: number[] = [];

        if (fieldMappings.colorField) {
            colorValues = data.map(d => typeof d[fieldMappings.colorField!] === 'number' ? d[fieldMappings.colorField!] : 0).filter(v => !isNaN(v));
        }
        if (fieldMappings.sizeField) {
            sizeValues = data.map(d => typeof d[fieldMappings.sizeField!] === 'number' ? d[fieldMappings.sizeField!] : 0).filter(v => !isNaN(v));
        }

        const colorMin = colorValues.length > 0 ? Math.min(...colorValues) : 0;
        const colorMax = colorValues.length > 0 ? Math.max(...colorValues) : 1;
        const sizeMin = sizeValues.length > 0 ? Math.min(...sizeValues) : 0;
        const sizeMax = sizeValues.length > 0 ? Math.max(...sizeValues) : 1;

        // Group by series if seriesField is specified
        const seriesMap = new Map<string, any[]>();
        const processed: any[] = [];

        data.forEach((row, idx) => {
            const x = row[fieldMappings.xField!];
            const y = row[fieldMappings.yField!];

            if (x === undefined || y === undefined) return;

            const seriesKey = fieldMappings.seriesField ? String(row[fieldMappings.seriesField]) : 'default';

            // Calculate normalized color value (0-1)
            let colorNorm = 0.5;
            if (fieldMappings.colorField && typeof row[fieldMappings.colorField] === 'number') {
                colorNorm = (row[fieldMappings.colorField] - colorMin) / (colorMax - colorMin || 1);
            }

            // Calculate marker size based on size field
            let markerSize = style.markerSize;
            if (fieldMappings.sizeField && typeof row[fieldMappings.sizeField] === 'number') {
                const sizeNorm = (row[fieldMappings.sizeField] - sizeMin) / (sizeMax - sizeMin || 1);
                markerSize = style.markerSizeMin + sizeNorm * (style.markerSizeMax - style.markerSizeMin);
            }

            // Calculate error bar values if enabled
            let errorY: [number, number] = [0, 0];
            if (errorBars?.show && errorBars.mode !== 'none') {
                if (errorBars.mode === 'constant') {
                    const constantVal = errorBars.constantValue || 1;
                    errorY = [constantVal, constantVal];
                } else if (errorBars.mode === 'field' && fieldMappings.yErrorField) {
                    const errorVal = Math.abs(Number(row[fieldMappings.yErrorField]) || 0);
                    errorY = [errorVal, errorVal];
                }
            }

            const point = {
                x: typeof x === 'number' ? x : idx,
                y: typeof y === 'number' ? y : 0,
                originalX: x,
                originalY: y,
                colorValue: fieldMappings.colorField ? row[fieldMappings.colorField] : null,
                colorNorm,
                sizeValue: fieldMappings.sizeField ? row[fieldMappings.sizeField] : null,
                markerSize,
                series: seriesKey,
                errorY,
                _originalRow: row
            };

            processed.push(point);

            if (!seriesMap.has(seriesKey)) {
                seriesMap.set(seriesKey, []);
            }
            seriesMap.get(seriesKey)!.push(point);
        });

        return {
            processedData: processed,
            seriesMap,
            colorScale: { min: colorMin, max: colorMax },
            sizeScale: { min: sizeMin, max: sizeMax },
            minValue: colorMin,
            maxValue: colorMax
        };
    }, [data, fieldMappings, style]);

    // Get color from colormap based on normalized value
    const getColorFromPalette = (norm: number): string => {
        // Default to weaverGold palette for WeaverBI theme
        const palette = SCIENTIFIC_COLOR_PALETTES[style.colormap as keyof typeof SCIENTIFIC_COLOR_PALETTES] || SCIENTIFIC_COLOR_PALETTES.weaverGold;
        const idx = Math.min(Math.floor(norm * (palette.length - 1)), palette.length - 1);
        return palette[Math.max(0, idx)];
    };

    // Series colors for categorical series
    const seriesColors = useMemo(() => {
        const colors: Record<string, string> = {};
        const palette = ['#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];
        let i = 0;
        seriesMap.forEach((_, key) => {
            colors[key] = palette[i % palette.length];
            i++;
        });
        return colors;
    }, [seriesMap]);

    // Axis formatter
    const formatAxis = (value: number): string => {
        if (axisOptions.axisFormat === 'scientific') {
            return value.toExponential(2);
        } else if (axisOptions.axisFormat === 'engineering') {
            const exp = Math.floor(Math.log10(Math.abs(value)) / 3) * 3;
            if (exp === 0 || !isFinite(exp)) return value.toFixed(2);
            return `${(value / Math.pow(10, exp)).toFixed(2)}e${exp}`;
        }
        return String(value);
    };

    // Animation style
    const animationStyle = animation.fadeIn ? {
        animation: `fadeIn ${animation.duration}ms ease-out`
    } : {};

    if (processedData.length === 0) {
        return (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
                <div className="text-center">
                    <p>No data to display</p>
                    <p className="text-sm text-gray-500 mt-1">Map X and Y fields to create a scatter plot</p>
                </div>
            </div>
        );
    }

    // Calculate domains with padding
    const calculateDomain = (axis: 'x' | 'y'): [number, number] => {
        if (!processedData.length) return [0, 100];

        const key = axis === 'x' ? 'x' : 'y';
        const isLog = axis === 'x' ? axisOptions.logScaleX : axisOptions.logScaleY;

        // Filter valid numbers - strict positive check for log scale
        const values = processedData
            .map(d => d[key])
            .filter(v => typeof v === 'number' && Number.isFinite(v) && (!isLog || v > 0));

        if (values.length === 0) return [0, 100];

        const min = Math.min(...values);
        const max = Math.max(...values);

        if (min === max) {
            if (isLog) {
                // For log scale, pad multiplicatively
                return [min * 0.5, max * 2];
            }
            const pad = min === 0 ? 1 : Math.abs(min) * 0.1;
            return [min - pad, max + pad];
        }

        if (isLog) {
            // Basic log padding
            return [min * 0.9, max * 1.1];
        }

        const range = max - min;
        return [min - range * 0.1, max + range * 0.1];
    };

    const xDomain = calculateDomain('x');
    const yDomain = calculateDomain('y');

    // Calculate Trendline
    const trendlineData = useMemo(() => {
        const type = scientificConfig.analysis?.trendline || 'none';
        if (type === 'none' || processedData.length < 2) return null;

        const validPoints = processedData.filter(d =>
            typeof d.x === 'number' && typeof d.y === 'number' && isFinite(d.x) && isFinite(d.y)
        ).sort((a, b) => a.x - b.x);

        if (validPoints.length < 2) return null;

        const xSeries = validPoints.map(d => d.x);
        const ySeries = validPoints.map(d => d.y);
        const n = validPoints.length;

        // Simple Linear Regression
        if (type === 'linear') {
            const sumX = xSeries.reduce((a, b) => a + b, 0);
            const sumY = ySeries.reduce((a, b) => a + b, 0);
            const sumXY = validPoints.reduce((acc, d) => acc + d.x * d.y, 0);
            const sumXX = xSeries.reduce((a, b) => a + b * b, 0);

            const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
            const intercept = (sumY - slope * sumX) / n;

            return validPoints.map(d => ({
                x: d.x,
                trend: slope * d.x + intercept
            }));
        }

        // Exponential Regression (y = a * e^(bx) -> ln(y) = ln(a) + bx)
        if (type === 'exp') {
            // Filter non-positive Ys
            const validExp = validPoints.filter(d => d.y > 0);
            if (validExp.length < 2) return null;

            const n = validExp.length;
            const sumX = validExp.reduce((a, b) => a + b.x, 0);
            const sumYLn = validExp.reduce((a, b) => a + Math.log(b.y), 0);
            const sumXYLn = validExp.reduce((acc, d) => acc + d.x * Math.log(d.y), 0);
            const sumXX = validExp.reduce((a, b) => a + b.x * b.x, 0);

            const slope = (n * sumXYLn - sumX * sumYLn) / (n * sumXX - sumX * sumX);
            const interceptLn = (sumYLn - slope * sumX) / n;
            const a = Math.exp(interceptLn);

            return validExp.map(d => ({
                x: d.x,
                trend: a * Math.exp(slope * d.x)
            }));
        }

        // Polynomial (2nd degree) using normal equations
        // y = ax² + bx + c
        if (type === 'poly2') {
            // Calculate sums for normal equations
            let sumX = 0, sumX2 = 0, sumX3 = 0, sumX4 = 0;
            let sumY = 0, sumXY = 0, sumX2Y = 0;

            validPoints.forEach(d => {
                const x = d.x;
                const y = d.y;
                const x2 = x * x;
                const x3 = x2 * x;
                const x4 = x2 * x2;

                sumX += x;
                sumX2 += x2;
                sumX3 += x3;
                sumX4 += x4;
                sumY += y;
                sumXY += x * y;
                sumX2Y += x2 * y;
            });

            // Solve 3x3 system using Cramer's rule
            // | n     sumX   sumX2  | | c |   | sumY   |
            // | sumX  sumX2  sumX3  | | b | = | sumXY  |
            // | sumX2 sumX3  sumX4  | | a |   | sumX2Y |

            const det = n * (sumX2 * sumX4 - sumX3 * sumX3)
                - sumX * (sumX * sumX4 - sumX3 * sumX2)
                + sumX2 * (sumX * sumX3 - sumX2 * sumX2);

            if (Math.abs(det) < 1e-10) {
                // Singular matrix, fall back to linear
                const linearSlope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
                const linearIntercept = (sumY - linearSlope * sumX) / n;
                return validPoints.map(d => ({ x: d.x, trend: linearSlope * d.x + linearIntercept }));
            }

            // Calculate coefficients using Cramer's rule
            const detC = sumY * (sumX2 * sumX4 - sumX3 * sumX3)
                - sumX * (sumXY * sumX4 - sumX3 * sumX2Y)
                + sumX2 * (sumXY * sumX3 - sumX2 * sumX2Y);

            const detB = n * (sumXY * sumX4 - sumX3 * sumX2Y)
                - sumY * (sumX * sumX4 - sumX3 * sumX2)
                + sumX2 * (sumX * sumX2Y - sumXY * sumX2);

            const detA = n * (sumX2 * sumX2Y - sumXY * sumX3)
                - sumX * (sumX * sumX2Y - sumXY * sumX2)
                + sumY * (sumX * sumX3 - sumX2 * sumX2);

            const polyC = detC / det;
            const polyB = detB / det;
            const polyA = detA / det;

            return validPoints.map(d => ({
                x: d.x,
                trend: polyA * d.x * d.x + polyB * d.x + polyC
            }));
        }

        return null;
    }, [processedData, scientificConfig.analysis?.trendline]);

    return (
        <div className="w-full h-full" style={animationStyle}>
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
            `}</style>
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart margin={{ top: 20, right: legend.show ? 120 : 30, bottom: 40, left: 60 }}>
                    <CartesianGrid
                        strokeDasharray={axisOptions.minorGrid ? "1 1" : "3 3"}
                        stroke={axisOptions.gridColor || '#333'}
                        strokeWidth={axisOptions.gridThickness || 1}
                        vertical={axisOptions.grid}
                        horizontal={axisOptions.grid}
                    />
                    <XAxis
                        type="number"
                        dataKey="x"
                        name={fieldMappings.xField}
                        scale={axisOptions.logScaleX ? 'log' : 'auto'}
                        domain={xDomain}
                        allowDataOverflow={true}
                        tick={{ fill: '#888', fontSize: 10 }}
                        tickFormatter={formatAxis}
                        stroke="#666"
                    >
                        <Label
                            value={axisOptions.labelX || fieldMappings.xField || 'X'}
                            position="bottom"
                            offset={15}
                            fill="#aaa"
                            fontSize={12}
                        />
                    </XAxis>
                    <YAxis
                        type="number"
                        dataKey="y"
                        name={fieldMappings.yField}
                        scale={axisOptions.logScaleY ? 'log' : 'auto'}
                        domain={yDomain}
                        allowDataOverflow={true}
                        tick={{ fill: '#888', fontSize: 10 }}
                        tickFormatter={formatAxis}
                        stroke="#666"
                    >
                        <Label
                            value={axisOptions.labelY || fieldMappings.yField || 'Y'}
                            angle={-90}
                            position="left"
                            offset={25}
                            fill="#aaa"
                            fontSize={12}
                        />
                    </YAxis>
                    {fieldMappings.sizeField && (
                        <ZAxis
                            type="number"
                            dataKey="markerSize"
                            range={[style.markerSizeMin * 10, style.markerSizeMax * 10]}
                            name={fieldMappings.sizeField}
                        />
                    )}
                    <Tooltip
                        contentStyle={{
                            backgroundColor: '#1a1a2e',
                            border: '1px solid #f59e0b33',
                            borderRadius: '8px',
                            color: '#fff',
                            boxShadow: '0 4px 20px rgba(245, 158, 11, 0.15)'
                        }}
                        cursor={{ strokeDasharray: '3 3', stroke: '#f59e0b' }}
                        formatter={(value: number, name: string) => [
                            axisOptions.axisFormat === 'scientific' ? value.toExponential(4) : value.toFixed(4),
                            name
                        ]}
                        labelFormatter={(label) => `Point: ${label}`}
                    />
                    {legend.show && (
                        <Legend
                            layout="vertical"
                            align="right"
                            verticalAlign="middle"
                            wrapperStyle={{ paddingLeft: 20 }}
                        />
                    )}

                    {/* Trendline */}
                    {trendlineData && (
                        <Line
                            data={trendlineData}
                            type="monotone"
                            dataKey="trend"
                            stroke="#ef4444"
                            strokeWidth={2}
                            strokeDasharray="5 5"
                            dot={false}
                            name="Trendline"
                            isAnimationActive={true}
                        />
                    )}

                    {/* Render each series */}
                    {Array.from(seriesMap.entries()).map(([seriesKey, seriesData]) => (
                        <Scatter
                            key={seriesKey}
                            name={seriesKey === 'default' ? (fieldMappings.yField || 'Data') : seriesKey}
                            data={seriesData}
                            fill={fieldMappings.colorField ? chartColor : (seriesColors[seriesKey] || chartColor)}
                            shape={(props) => (
                                <ScientificMarker
                                    {...props}
                                    shape={style.markerShape}
                                    size={props.payload?.markerSize || style.markerSize}
                                    outline={style.markerOutline}
                                    fill={fieldMappings.colorField
                                        ? getColorFromPalette(props.payload?.colorNorm || 0.5)
                                        : (seriesColors[seriesKey] || chartColor)
                                    }
                                    alpha={style.alpha}
                                />
                            )}
                            onClick={(data) => onPointClick?.(data.payload)}
                        >
                            {/* Error Bars - only show if enabled */}
                            {errorBars?.show && errorBars.mode !== 'none' && (
                                <ErrorBar
                                    dataKey="errorY"
                                    width={errorBars.capSize || 5}
                                    strokeWidth={errorBars.lineThickness || 1}
                                    stroke={errorBars.color || chartColor}
                                    direction="y"
                                />
                            )}
                        </Scatter>
                    ))}
                </ComposedChart>
            </ResponsiveContainer>

            {/* Colorbar for color-encoded data */}
            {fieldMappings.colorField && style.showColorbar && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-40 flex flex-col">
                    <div className="text-xs text-gray-400 mb-1 text-right">{maxValue.toFixed(2)}</div>
                    <div
                        className="flex-1 rounded"
                        style={{
                            background: `linear-gradient(to bottom, ${(SCIENTIFIC_COLOR_PALETTES[style.colormap as keyof typeof SCIENTIFIC_COLOR_PALETTES] || SCIENTIFIC_COLOR_PALETTES.viridis)
                                .slice().reverse().join(', ')
                                })`
                        }}
                    />
                    <div className="text-xs text-gray-400 mt-1 text-right">{minValue.toFixed(2)}</div>
                    <div className="text-xs text-gray-500 mt-2 text-center rotate-90 origin-top-left translate-x-8">
                        {fieldMappings.colorField}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ScientificScatterPlot;
